import { BaseTextFieldBox } from "@/shared/components/text-box/base-text-box";
import { BaseDescriptionTextFieldBox } from "@/shared/components/text-box/base-description-text-box";
import React, { useEffect, useState } from "react";
import { utilities } from "@/shared/utils/utility";
import { useInputValidation, ValidationRule } from "@/shared/hooks/use-input-validation";

interface Props {
  id?: string;
  className?: string;
  title?: string;
  placeholder?: string;
  value?: string;
  initialValue?: string;
  onChange: (newValue: string) => void;
  errorMessage?: string;
  labelEnable?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  descriptionType?: boolean;
}

export const AmountForm = (props: Props) => {
  const {
    id = "",
    className = "",
    title = "",
    placeholder = "",
    value = "",
    initialValue,
    onChange,
    errorMessage = "",
    labelEnable = false,
    isDisabled = false,
    isReadOnly = false,
    descriptionType = false,
  } = props;

  const [showValue, setShowValue] = useState(initialValue ?? "");
  const [isComposing, setIsComposing] = useState(false);

  const { validate } = useInputValidation("numericAndHyphenOnly");

  /**
   * 全角 → 半角変換
   */
  const toHalfWidth = (value: string): string => {
    return value
      .replace(/[０-９－]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - 0xfee0)
      )
      .replace(/[ーｰ‐‑–—―]/g, "-");
  };

  /**
   * 数値整形（バリデーション + 桁数制限）
   */
  const normalizeNumber = (value: string): string => {
  let cleaned = toHalfWidth(value)
    .replace(/[^0-9-]/g, "")
    .replace(/(?!^)-/g, "");

  const isNegative = cleaned.startsWith("-");
  const numericPart = cleaned.replace("-", "");

  const limited = numericPart.slice(0, 15);

  return isNegative ? `-${limited}` : limited;
};

  /**
   * カンマ区切り
   */
  const formatWithComma = (value: string): string => {
    if (!value) return "";

    const isNegative = value.startsWith("-");
    const numberPart = value.replace("-", "");

    const formatted = numberPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return isNegative ? `-${formatted}` : formatted;
  };

  /**
   * 値変更ハンドラ（イベント非依存）
   */
  const handleValueChange = (rawValue: string, composing = isComposing) => {
    // IME入力中はそのまま表示のみ
    if (composing) {
      setShowValue(rawValue);
      return;
    }

    const normalized = normalizeNumber(rawValue);

    setShowValue(formatWithComma(normalized));
    onChange(normalized);
  };

  /**
   * 初期値セット
   */
  useEffect(() => {
    if (initialValue !== undefined) {
      const normalized = normalizeNumber(initialValue);
      setShowValue(formatWithComma(normalized));
    }
  }, [initialValue]);

  if (descriptionType) {
    return (
      <BaseDescriptionTextFieldBox
        id={id}
        className={className}
        title={title}
        placeholder={placeholder}
        value={showValue}
        type="text"
        onChange={(e) => handleValueChange(e.target.value)}
        errorMessage={errorMessage}
        labelEnable={labelEnable}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={(e) => {
          setIsComposing(false);
          // IME確定時は強制的に検証ルールを適用
          handleValueChange(e.currentTarget.value, false);
        }}
    />
  )} else {
    return (
      <BaseTextFieldBox
        id={id}
        className={className}
        title={title}
        placeholder={placeholder}
        value={showValue}
        type="text"
        onChange={(e) => handleValueChange(e.target.value)}
        errorMessage={errorMessage}
        labelEnable={labelEnable}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={(e) => {
          setIsComposing(false);
          // IME確定時は強制的に検証ルールを適用
          handleValueChange(e.currentTarget.value, false);
        }}
      />
    )
  }
};
